"use client";

import { memo, useCallback, useEffect, useRef, useState } from "react";



import { getAllFormEntities, updateFormEntities } from "@/api/entity";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HashLoader } from "react-spinners";
import { useDebouncedCallback } from "use-debounce";



import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";


interface Props {
  formId: number;
  isOpen: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 10;

const EntitiesListModal: React.FC<Props> = ({ isOpen, onClose, formId }) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>انتخاب جداول</DialogTitle>
        </DialogHeader>

        <Content formId={formId} onClose={onClose} isOpen={isOpen} />
      </DialogContent>
    </Dialog>
  );
};

const Content = memo(
  ({
    formId,
    onClose,
    isOpen
  }: {
    formId: number;
    onClose: () => void;
    isOpen: boolean;
  }) => {
    const [search, setSearch] = useState("");
    const [selectedEntities, setSelectedEntities] = useState<number[]>([]);

    const initializedRef = useRef(false);

    const queryClient = useQueryClient();
    const observerRef = useRef<IntersectionObserver | null>(null);

    // 🔹 Infinite Query
    const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
      useInfiniteQuery({
        queryKey: ["form-entities", formId, search],
        queryFn: ({ pageParam = 1 }) =>
          getAllFormEntities(formId, {
            size: PAGE_SIZE,
            page: pageParam,
            search
          }),
        getNextPageParam: (lastPage, pages) => {
          const currentItems = lastPage?.data ?? [];
          
          if (currentItems.length < PAGE_SIZE) {
            return undefined;
          }

          return pages.length + 1;
        },
        initialPageParam: 1
      });

    // 🔹 flatten data
    const entities =
      data?.pages
        .filter(
          (p): p is NonNullable<typeof p> => p !== null && p !== undefined
        )
        .flatMap((p) => p.data) ?? [];

    // 🔹 init selected فقط یکبار
    useEffect(() => {
      if (initializedRef.current) return;

      // تا وقتی دیتا نیامده کاری نکن
      if (!entities.length) return;

      const preSelected = entities.filter((x) => x.isAccess).map((x) => x.id);

      setSelectedEntities(preSelected);

      console.log("ffffffffff", preSelected);

      initializedRef.current = true;
    }, [entities]);

    // 🔹 reset هنگام بستن modal
    useEffect(() => {
      if (!isOpen) {
        initializedRef.current = false;
        setSelectedEntities([]);
        setSearch("");
      }
    }, [isOpen]);

    // 🔹 toggle checkbox
    const changeHandler = (id: number) => {
      setSelectedEntities((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    };

    // 🔹 search debounce
    const handleSearch = useDebouncedCallback((value: string) => {
      setSearch(value);
      initializedRef.current = false;
    }, 800);

    // 🔹 mutation
    const { mutate, isPending } = useMutation({
      mutationFn: () => updateFormEntities(formId, selectedEntities),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["form-entities", formId]
        });

        onClose();
      }
    });

    // 🔹 infinite scroll observer
    const lastItemRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (isFetchingNextPage) return;

        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        });

        if (node) {
          observerRef.current.observe(node);
        }
      },
      [isFetchingNextPage, hasNextPage, fetchNextPage]
    );

    return (
      <>
        {/* 🔍 Search */}
        <Input
          placeholder="جستجو"
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full md:w-1/2"
        />

        {/* 📜 List */}
        <div className="mt-5 h-64 divide-y divide-slate-300 overflow-y-auto rounded-md border border-slate-300">
          {isFetching && !entities.length ? (
            <div className="flex h-full items-center justify-center">
              <HashLoader color="#0099A5" size={40} />
            </div>
          ) : (
            <>
              {entities.map((item, index) => {
                const isLast = index === entities.length - 1;

                return (
                  <div
                    key={item.id}
                    ref={isLast ? lastItemRef : null}
                    className="flex items-center gap-x-2 px-2 py-4"
                  >
                    <Checkbox
                      checked={selectedEntities.includes(item.id)}
                      onCheckedChange={() => changeHandler(item.id)}
                    />

                    <p className="line-clamp-1 text-sm">{item.name}</p>
                  </div>
                );
              })}

              {/* 🔄 loading more */}
              {isFetchingNextPage && (
                <div className="py-3 text-center text-sm text-gray-500">
                  در حال بارگذاری...
                </div>
              )}
            </>
          )}
        </div>

        {/* 💾 Save */}
        <Button
          className="mt-5 w-full"
          onClick={() => mutate()}
          disabled={isPending}
        >
          ذخیره
        </Button>
      </>
    );
  }
);

export default EntitiesListModal;